import useThrottledCallback from 'beautiful-react-hooks/useThrottledCallback';
import useViewportSpy from 'beautiful-react-hooks/useViewportSpy';
import clsx from 'clsx';
import type { ReactNode, CSSProperties, ComponentType } from 'react';
import { useRef, useState, useEffect, memo } from 'react';

const defaultOptions = {
  rootMargin: '25% 25%',
};

export type WindowizerProps = {
  hideOnExit?: boolean;
  className?: string;
  hiddenClassName?: string;
  loadedClassName?: string;
  intersectionOptions?: IntersectionObserverInit;
  children?: ReactNode | null;
};

const Windowizer = ({
  hideOnExit = true,
  className,
  hiddenClassName,
  loadedClassName,
  intersectionOptions,
  children,
}: WindowizerProps) => {
  const spyRef = useRef<HTMLDivElement>(null);
  const isVisible = useViewportSpy(spyRef, { ...defaultOptions, ...intersectionOptions });
  const savedShow = useRef(false);
  const [show, setShow] = useState(false);
  const [style, setStyle] = useState<CSSProperties>(null);

  const throttledUpdate = useThrottledCallback(
    (visible: boolean) => {
      if (visible) {
        setStyle(null);
        setShow(true);
        savedShow.current = true;
      } else if (hideOnExit && savedShow.current) {
        savedShow.current = false;
        setStyle({
          width: spyRef.current?.clientWidth,
          height: spyRef.current?.clientHeight,
        });
        setShow(false);
      }
    },
    [hideOnExit],
    200,
  );

  useEffect(() => {
    throttledUpdate(isVisible);

    return () => {
      throttledUpdate.cancel();
    };
  }, [isVisible, throttledUpdate]);

  return (
    <div className={clsx(className, show && loadedClassName, !show && hiddenClassName)} style={style} ref={spyRef}>
      {show && children}
    </div>
  );
};

export default memo(Windowizer);

export type WindowizerHocProps = {
  windowizeHideOnExit?: boolean;
  windowizeClassName?: string;
  windowizeHiddenClassName?: string;
  windowizeLoadedClassName?: string;
  intersectionOptions?: IntersectionObserverInit;
};

export function windowized<T>(BaseComponent: ComponentType<T>): ComponentType<T & WindowizerHocProps> {
  return function WrappedComp(props: T & WindowizerHocProps) {
    return (
      <Windowizer
        hideOnExit={props.windowizeHideOnExit}
        className={props.windowizeClassName}
        hiddenClassName={props.windowizeHiddenClassName}
        loadedClassName={props.windowizeLoadedClassName}
        intersectionOptions={props.intersectionOptions}
      >
        <BaseComponent {...props} />
      </Windowizer>
    );
  };
}
